import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config/canonical';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { RefreshCw, TrendingUp, Users, BarChart3, PieChart as PieIcon } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0F172A', '#1E40AF', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#6366F1', '#EC4899'];
const STATUS_COLORS_MAP = {
  NEW: '#3B82F6', ACTIVE: '#22C55E', 'PENDING REVIEW': '#EAB308', ASSIGNED: '#8B5CF6',
  'EVIDENCE COLLECTION': '#06B6D4', COMPLETED: '#10B981', CLOSED: '#64748B', 'UNDER REVIEW': '#6366F1',
  'AWAITING CLIENT INFO': '#F97316'
};

const Analytics = () => {
  const token = localStorage.getItem('nexus_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading || !data) return <div className="text-center py-12 text-slate-400">Loading analytics...</div>;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="analytics-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[10px] font-mono text-slate-400 uppercase tracking-[0.2em] mb-1">INTELLIGENCE ANALYTICS</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900 tracking-tight">Case Analytics & Performance</h1>
        </div>
        <Button variant="outline" className="border-slate-300 text-slate-600" onClick={fetchAnalytics}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-5 h-5 text-[#0F172A]" />
            <span className="text-2xl font-heading font-bold text-slate-900">{data.total_cases}</span>
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Cases</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <PieIcon className="w-5 h-5 text-blue-600" />
            <span className="text-2xl font-heading font-bold text-slate-900">{data.status_distribution?.length || 0}</span>
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status Types</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-heading font-bold text-slate-900">{data.type_distribution?.length || 0}</span>
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Case Types</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-2xl font-heading font-bold text-slate-900">{data.team_performance?.length || 0}</span>
          </div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Team Members</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution Pie */}
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4">Case Status Distribution</h3>
          {data.status_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.status_distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                  paddingAngle={2} dataKey="value" nameKey="name">
                  {data.status_distribution.map((entry, i) => (
                    <Cell key={`status-${entry.name}`} fill={STATUS_COLORS_MAP[entry.name] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </div>

        {/* Case Type Distribution Bar */}
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4">Cases by Type</h3>
          {data.type_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.type_distribution} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} width={120} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '12px' }} />
                <Bar dataKey="value" fill="#0F172A" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
        </div>
      </div>

      {/* Monthly Trend */}
      {data.monthly_trend?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4">Monthly Case Volume</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '12px' }} />
              <Bar dataKey="cases" fill="#1E40AF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Team Performance */}
      {data.team_performance?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4">Team Performance</h3>
          <div className="space-y-0">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-mono text-slate-400 uppercase tracking-[0.15em]">
              <div className="col-span-5">Name</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-2">Cases</div>
              <div className="col-span-2">Load</div>
            </div>
            {data.team_performance.map((member, i) => (
              <div key={`team-${member.name}-${member.role}`} className={`grid grid-cols-12 gap-4 px-4 py-2.5 items-center ${i < data.team_performance.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <div className="col-span-5 text-sm font-medium text-slate-800">{member.name}</div>
                <div className="col-span-3">
                  <Badge className="bg-slate-100 text-slate-600 text-[10px]">{member.role?.replace(/_/g, ' ')}</Badge>
                </div>
                <div className="col-span-2 text-sm font-mono text-slate-700">{member.assigned_cases}</div>
                <div className="col-span-2">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-[#0F172A] h-2 rounded-full" style={{ width: `${Math.min(member.assigned_cases * 20, 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Priority Distribution */}
      {data.priority_distribution?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4">Priority Breakdown</h3>
          <div className="flex flex-wrap gap-4">
            {data.priority_distribution.map((p, i) => (
              <div key={`priority-${p.name}`} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
                <div className={`w-3 h-3 rounded-full ${p.name === 'URGENT' ? 'bg-red-500' : p.name === 'HIGH' ? 'bg-orange-500' : p.name === 'NORMAL' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                <div>
                  <p className="text-sm font-medium text-slate-800">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.value} cases</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
